import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Suspense, useEffect, useState } from 'react';
import { TenantProvider } from './contexts/TenantContext';
import { CartProvider } from './contexts/CartContext';
import { ToastProvider } from './components/common/Toast';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { TemplateRouteGuard } from './components/routing/TemplateRouteGuard';
import { SingleProductGate } from './components/routing/SingleProductGate';
import { analytics } from '../src/utils/analytics';
import { facebookPixel } from '../src/utils/facebookPixel';
import { AffiliateTracker } from './components/AffiliateTracker';
import { NewsletterFormCapture } from './components/NewsletterFormCapture';
import { routes } from './config/routes';
import { Loading } from './components/common/Loading';
import { usePageTracking } from './hooks/usePageTracking';
import { ApiError } from './services/api';

/**
 * Bridge unhandled promise rejections (including ApiErrors that escape a
 * caller's `.catch`) into the React error boundary. React 18 error
 * boundaries only catch RENDER errors — without this hook, a rejected
 * API call from inside a `useEffect` or click handler would silently log
 * to the console and leave the user staring at a frozen UI.
 *
 * Strategy: subscribe to `unhandledrejection`, then rethrow during render
 * via a state setter. The next render throws, ErrorBoundary catches it,
 * and the user sees a real error screen instead of a blank one.
 */
function GlobalApiErrorCatcher() {
  const [boundaryError, setBoundaryError] = useState<Error | null>(null);

  useEffect(() => {
    const onUnhandled = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (reason instanceof ApiError) {
        // Don't capture 401s — those redirect to login via the auth flow.
        if (reason.statusCode === 401) return;
        event.preventDefault();
        setBoundaryError(reason);
      } else if (reason instanceof Error) {
        event.preventDefault();
        setBoundaryError(reason);
      }
    };
    window.addEventListener('unhandledrejection', onUnhandled);
    return () => window.removeEventListener('unhandledrejection', onUnhandled);
  }, []);

  if (boundaryError) {
    // Synchronously throw during render so ErrorBoundary catches.
    throw boundaryError;
  }
  return null;
}

// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();

  // Use internal page tracking with filtering
  usePageTracking();

  useEffect(() => {
    // Track Google Analytics
    analytics.pageView(location.pathname + location.search);

    // Track Facebook Pixel PageView if enabled
    if (facebookPixel.isInitialized()) {
      const eventSettings = (window as any).__FB_PIXEL_EVENT_SETTINGS__ || {};
      const isPageViewEnabled = eventSettings.pageView !== false; // Default to true

      if (isPageViewEnabled) {
        facebookPixel.pageView();
      }
    }

    // Dispatch custom event for branding updates (for funnel navigation)
    window.dispatchEvent(new Event('route-changed'));
  }, [location]);

  return null;
}

// Loading fallback for lazy components
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <Loading fullScreen={true} text="Loading..." />
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <GlobalApiErrorCatcher />
      <TenantProvider>
        <CartProvider>
          <ToastProvider>
            <Router>
              <AnalyticsTracker />
              <AffiliateTracker />
              <NewsletterFormCapture />
              <TemplateRouteGuard>
                <SingleProductGate>
                  <Suspense fallback={<LoadingFallback />}>
                    <Routes>
                      {routes.map((route) => {
                        const RouteElement = route.element;
                        if (!RouteElement) return null;

                        return (
                          <Route
                            key={route.path}
                            path={route.path}
                            element={<RouteElement />}
                          />
                        );
                      })}
                    </Routes>
                  </Suspense>
                </SingleProductGate>
              </TemplateRouteGuard>
            </Router>
          </ToastProvider>
        </CartProvider>
      </TenantProvider>
    </ErrorBoundary>
  );
}

export default App;
